package com.sddamico.swigmore.api

import com.sddamico.swigmore.models.AdminBallotsResponse
import com.sddamico.swigmore.models.AdminSaveBallotRequest
import com.sddamico.swigmore.models.AutoSendRequest
import com.sddamico.swigmore.models.BallotIdRequest
import com.sddamico.swigmore.models.BallotReviewRequest
import com.sddamico.swigmore.models.BallotReviewResponse
import com.sddamico.swigmore.models.ComputerPollResponse
import com.sddamico.swigmore.models.CopyEditRequest
import com.sddamico.swigmore.models.CurrentPollResponse
import com.sddamico.swigmore.models.EmailRequest
import com.sddamico.swigmore.models.GameResultsResponse
import com.sddamico.swigmore.models.LeagueResponse
import com.sddamico.swigmore.models.LogoutRequest
import com.sddamico.swigmore.models.MatchupsResponse
import com.sddamico.swigmore.models.MeResponse
import com.sddamico.swigmore.models.MemberDto
import com.sddamico.swigmore.models.MembersResponse
import com.sddamico.swigmore.models.MyBallotDto
import com.sddamico.swigmore.models.NotifyPreviewResponse
import com.sddamico.swigmore.models.OpenWeekRequest
import com.sddamico.swigmore.models.PollArchiveResponse
import com.sddamico.swigmore.models.PollResultsResponse
import com.sddamico.swigmore.models.PollWeekDto
import com.sddamico.swigmore.models.PushSubscribeRequest
import com.sddamico.swigmore.models.PushTestResponse
import com.sddamico.swigmore.models.SaveMemberRequest
import com.sddamico.swigmore.models.SendOtpRequest
import com.sddamico.swigmore.models.SessionResponse
import com.sddamico.swigmore.models.SubmitBallotRequest
import com.sddamico.swigmore.models.SuccessResponse
import com.sddamico.swigmore.models.VapidKeyResponse
import com.sddamico.swigmore.models.VerifyRequest
import com.sddamico.swigmore.models.WeekRequest

/** A poll week by season and Fantrax week. */
data class WeekRef(val season: Int, val week: Int)

val PollWeekDto.ref get() = WeekRef(season, week)

/** Every server endpoint the browser calls. Failures throw [ApiException]. */
object PollApi {
  suspend fun me(): MeResponse? = apiGet("auth/me", SessionResponse.serializer()).me

  /** Ends the session and drops [pushEndpoint], this browser's push subscription, from the account. */
  suspend fun logout(pushEndpoint: String?) {
    apiPost("auth/logout", LogoutRequest(pushEndpoint), LogoutRequest.serializer(), SuccessResponse.serializer())
  }

  suspend fun sendOtp(email: String) {
    apiPost("auth/send-otp", SendOtpRequest(email), SendOtpRequest.serializer(), SuccessResponse.serializer())
  }

  suspend fun verify(email: String, code: String) {
    apiPost("auth/verify", VerifyRequest(email, code), VerifyRequest.serializer(), SuccessResponse.serializer())
  }

  suspend fun league(): LeagueResponse = apiGet("league", LeagueResponse.serializer())

  suspend fun matchups(): MatchupsResponse = apiGet("league/matchups", MatchupsResponse.serializer())

  suspend fun gameResults(): GameResultsResponse = apiGet("league/results", GameResultsResponse.serializer())

  suspend fun computerPoll(): ComputerPollResponse = apiGet("league/computer-poll", ComputerPollResponse.serializer())

  suspend fun current(): CurrentPollResponse = apiGet("poll/current", CurrentPollResponse.serializer())

  /**
   * The published poll for [week], or the latest one. [preview] re-tallies the current ballots instead,
   * which only commissioners may see.
   */
  suspend fun results(week: WeekRef? = null, preview: Boolean = false): PollResultsResponse =
    apiGet("poll/results", PollResultsResponse.serializer(),
      "season" to week?.season, "week" to week?.week, "preview" to preview.takeIf { it })

  /** [season] defaults to the latest one with a published poll. */
  suspend fun archive(season: Int?): PollArchiveResponse =
    apiGet("poll/archive", PollArchiveResponse.serializer(), "season" to season)

  suspend fun submitBallot(teamIds: List<String>): MyBallotDto =
    apiPost("poll/ballot", SubmitBallotRequest(teamIds), SubmitBallotRequest.serializer(), MyBallotDto.serializer())

  /** Claude's review of the ballot as it stands, [slots] in order with null for an empty slot. */
  suspend fun reviewBallot(slots: List<String?>): BallotReviewResponse =
    apiPost("poll/ballot-review", BallotReviewRequest(slots), BallotReviewRequest.serializer(), BallotReviewResponse.serializer())

  suspend fun withdrawBallot() {
    apiPost("poll/ballot-withdraw", SuccessResponse.serializer())
  }

  suspend fun members(): List<MemberDto> = apiGet("admin/members", MembersResponse.serializer()).members

  suspend fun saveMember(member: SaveMemberRequest) {
    apiPost("admin/members", member, SaveMemberRequest.serializer(), MembersResponse.serializer())
  }

  suspend fun deleteMember(email: String) {
    apiPost("admin/member-delete", EmailRequest(email), EmailRequest.serializer(), SuccessResponse.serializer())
  }

  suspend fun ballots(): AdminBallotsResponse = apiGet("admin/ballots", AdminBallotsResponse.serializer())

  /** Enters or replaces [voterEmail]'s ballot as the commissioner. */
  suspend fun saveBallot(week: WeekRef, voterEmail: String, teamIds: List<String>) {
    apiPost("admin/ballot-save", AdminSaveBallotRequest(week.season, week.week, voterEmail, teamIds),
      AdminSaveBallotRequest.serializer(), MyBallotDto.serializer())
  }

  suspend fun deleteBallot(ballotId: String) {
    apiPost("admin/ballot-delete", BallotIdRequest(ballotId), BallotIdRequest.serializer(), SuccessResponse.serializer())
  }

  /** Opens [week] of the current season. */
  suspend fun openWeek(week: Int) {
    apiPost("admin/week-open", OpenWeekRequest(week), OpenWeekRequest.serializer(), PollWeekDto.serializer())
  }

  suspend fun publish(week: WeekRef) {
    apiPost("admin/publish", WeekRequest(week.season, week.week), WeekRequest.serializer(), PollResultsResponse.serializer())
  }

  /** Off holds each week's announcement until [sendAnnouncement]. */
  suspend fun setAutoSend(autoSend: Boolean) {
    apiPost("admin/auto-send", AutoSendRequest(autoSend), AutoSendRequest.serializer(), SuccessResponse.serializer())
  }

  /** Sends [week]'s announcement, held while auto-send was off or failed. */
  suspend fun sendAnnouncement(week: WeekRef) {
    apiPost("notify/send", WeekRequest(week.season, week.week), WeekRequest.serializer(), SuccessResponse.serializer())
  }

  /** Starts a Fantrax sync; [LeagueResponse.sync] reports when it finishes. 409 while one is running. */
  suspend fun syncFantrax() {
    apiPost("admin/fantrax-sync", SuccessResponse.serializer())
  }

  /** The announcement for [week], or the open week. */
  suspend fun announcement(week: WeekRef?): NotifyPreviewResponse =
    apiGet("notify/preview", NotifyPreviewResponse.serializer(), "season" to week?.season, "week" to week?.week)

  suspend fun saveAnnouncementCopy(edit: CopyEditRequest): NotifyPreviewResponse =
    apiPost("notify/copy", edit, CopyEditRequest.serializer(), NotifyPreviewResponse.serializer())

  suspend fun rewriteAnnouncement(week: WeekRequest): NotifyPreviewResponse =
    apiPost("notify/rewrite", week, WeekRequest.serializer(), NotifyPreviewResponse.serializer())

  /** Null when push is off on this server. */
  suspend fun vapidKey(): String? = apiGet("push/vapid-key", VapidKeyResponse.serializer()).publicKey

  suspend fun subscribePush(subscription: PushSubscribeRequest) {
    apiPost("push/subscribe", subscription, PushSubscribeRequest.serializer(), SuccessResponse.serializer())
  }

  suspend fun testPush(): PushTestResponse = apiPost("push/test", PushTestResponse.serializer())
}
